import { WORK_ORDER_BUNDLE_STATUS } from "constants/index";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { WORK_ORDER_STATUS_LABEL } from "helpers/work_orders";
import {
  IMovementWorkOrderBundleDetail,
  IMovementWorkOrderBundleFractionationDetail,
  IMovementWorkOrderDetail,
  Movement,
} from "types";

type Props = {
  movement: Movement;
  order: IMovementWorkOrderDetail;
};

const workOrderTypeTitle = (
  type: IMovementWorkOrderDetail["work_order_type"],
) => {
  switch (type) {
    case "fractionation":
      return "Fraccionado";
    default:
      return "Orden de trabajo";
  }
};

function FractionationDetails({ order }: { order: IMovementWorkOrderDetail }) {
  const rows = order.bundles.flatMap((bundle) => {
    const groups: Array<{
      source: string;
      values: IMovementWorkOrderBundleFractionationDetail;
      bundle: IMovementWorkOrderBundleDetail;
    }> = [{ source: "Disponible", values: bundle.available, bundle }];
    if (bundle.not_available) {
      groups.push({
        source: "No disponible",
        values: bundle.not_available,
        bundle,
      });
    }
    return groups;
  });

  const totals = rows.reduce(
    (current, { values }) => ({
      amount: current.amount + values.amount,
      packages: current.packages + values.packages,
      loose: current.loose + values.loose,
      scrap: current.scrap + values.scrap,
    }),
    { amount: 0, packages: 0, loose: 0, scrap: 0 },
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ border: "1px solid grey", p: 1, overflow: "visible" , borderRadius: "0.5rem" }}
    >
      <Typography variant="h6" gutterBottom>
        Fardos
      </Typography>
      <Table
        size="small"
        aria-label="Detalle de fraccionado de la orden de trabajo"
        sx={{
          "& thead": { display: "table-header-group" },
          "& tr": { breakInside: "avoid", pageBreakInside: "avoid" },
          "& td, & th": { color: "#000", borderColor: "#999" },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Fardo</TableCell>
            <TableCell>Artículo</TableCell>
            <TableCell>Descripción</TableCell>
            <TableCell>Lote</TableCell>
            <TableCell>Ubicación</TableCell>
            <TableCell>Origen</TableCell>
            <TableCell align="right">Unidades</TableCell>
            <TableCell align="right">Paquetes</TableCell>
            <TableCell align="right">Sueltos</TableCell>
            <TableCell align="right">Scrap</TableCell>
            <TableCell>Estado</TableCell>
              <TableCell>Operario</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ bundle, source, values }) => (
            <TableRow key={`${bundle.id}-${source}`}>
              <TableCell>F-{bundle.lote_externo}</TableCell>
              <TableCell>{bundle.article.code}</TableCell>
              <TableCell>{bundle.article.description}</TableCell>
              <TableCell>{bundle.lote || bundle.lote_externo || "-"}</TableCell>
              <TableCell>{bundle.location?.code || "-"}</TableCell>
              <TableCell>{source}</TableCell>
              <TableCell align="right">{values.amount}</TableCell>
              <TableCell align="right">{values.packages}</TableCell>
              <TableCell align="right">{values.loose}</TableCell>
              <TableCell align="right">{values.scrap}</TableCell>
              <TableCell>
                {bundle.completed ? "Completo" : bundle.status === WORK_ORDER_BUNDLE_STATUS.IN_PROCESS ? "En trabajo" : "Pendiente"}
              </TableCell>
              <TableCell>{bundle.operator ? `${bundle.operator.first_name} ${bundle.operator.last_name}` : "—"}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={6}>
              <b>Total</b>
            </TableCell>
            <TableCell align="right">
              <b>{totals.amount}</b>
            </TableCell>
            <TableCell align="right">
              <b>{totals.packages}</b>
            </TableCell>
            <TableCell align="right">
              <b>{totals.loose}</b>
            </TableCell>
            <TableCell align="right">
              <b>{totals.scrap}</b>
            </TableCell>
            <TableCell /><TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function WorkOrderOperationsDetails({ movement, order }: Props) {
  return (
    <Grid item xs={12} display="flex" flexDirection="column" gap={2}>
      <Grid
        container
        component={Paper}
        padding={2}
        sx={{ border: "1px solid grey", borderRadius: "0.5rem" }}
      >
        <Grid item xs={12}>
          <Typography variant="h6">
            {workOrderTypeTitle(order.work_order_type)} · OT #
            {order.movement.id}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Estado:</b> {WORK_ORDER_STATUS_LABEL[order.status]}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Progreso:</b> {order.completed_bundles} de {order.total_bundles}{" "}
            fardos completados
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Cliente:</b> {movement.client?.razon_social || "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Doc. del cliente:</b> {movement.client_document || "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Cliente asociado:</b> {movement.associated_client || "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Remito:</b> {movement.numero_remito || "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Pallets:</b> {movement.pallets ?? "-"}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="body2">
            <b>Peso:</b> {movement.weight ?? "-"}
          </Typography>
        </Grid>
        {movement.observation && (
          <Grid item xs={12}>
            <Typography variant="body2">
              <b>Observaciones:</b> {movement.observation}
            </Typography>
          </Grid>
        )}
      </Grid>
      {order.work_order_type === "fractionation" ? (
        <FractionationDetails order={order} />
      ) : (
        <Typography variant="body2">
          Este tipo de orden todavía no tiene un reporte de detalle.
        </Typography>
      )}
    </Grid>
  );
}
